import type { LscMember, LscMemberCounts } from '../types';

export type MemberStatusFilter = 'current' | 'pending_link' | 'inactive' | 'all';

export const MEMBER_STATUS_FILTER_LABELS: Record<MemberStatusFilter, string> = {
  current: 'Current',
  pending_link: 'Pending Link',
  inactive: 'Inactive',
  all: 'All',
};

export const DEFAULT_MEMBER_STATUS_FILTER: MemberStatusFilter = 'current';

export const INACTIVE_MEMBER_STATUS = 'inactive';
export const PENDING_LINK_MEMBER_STATUS = 'imported_unclaimed';
export const MERGED_DEACTIVATION_REASON = 'duplicate_merged_into_imported_member';
export const COMMISSIONER_REMOVED_DEACTIVATION_REASON = 'commissioner_removed';

export function isInactiveMember(member: LscMember): boolean {
  return member.status === INACTIVE_MEMBER_STATUS;
}

export function isPendingLinkMember(member: LscMember): boolean {
  return member.status === PENDING_LINK_MEMBER_STATUS;
}

export function isCurrentMember(member: LscMember): boolean {
  return !isInactiveMember(member);
}

export function isMergedInactiveMember(member: LscMember): boolean {
  return (
    isInactiveMember(member) &&
    (member.deactivation_reason === MERGED_DEACTIVATION_REASON ||
      Boolean(member.merged_into_member_id))
  );
}

export function filterMembersByStatus(
  members: LscMember[],
  filter: MemberStatusFilter
): LscMember[] {
  switch (filter) {
    case 'current':
      return members.filter(isCurrentMember);
    case 'pending_link':
      return members.filter(isPendingLinkMember);
    case 'inactive':
      return members.filter(isInactiveMember);
    case 'all':
      return members;
    default:
      return members;
  }
}

export function countMembersByFilter(
  members: LscMember[]
): Record<MemberStatusFilter, number> {
  return {
    current: members.filter(isCurrentMember).length,
    pending_link: members.filter(isPendingLinkMember).length,
    inactive: members.filter(isInactiveMember).length,
    all: members.length,
  };
}

export function emptyFilterMessage(filter: MemberStatusFilter): string {
  switch (filter) {
    case 'current':
      return 'No current members in this league workspace.';
    case 'pending_link':
      return 'No members are waiting for identity linkage.';
    case 'inactive':
      return 'No inactive members in this league workspace.';
    case 'all':
      return 'No members in this league workspace yet.';
    default:
      return 'No members match this filter.';
  }
}

export function computeMemberCounts(members: LscMember[]): LscMemberCounts {
  const inactive = members.filter(isInactiveMember).length;
  const active = members.filter((member) => member.status === 'active').length;
  const pendingLink = members.filter(isPendingLinkMember).length;
  const invited = members.filter((member) => member.status === 'invited').length;
  const total = members.length;
  return {
    current_member_count: total - inactive,
    active_linked_member_count: active,
    pending_link_member_count: pendingLink,
    inactive_member_count: inactive,
    invited_member_count: invited,
    total_member_count: total,
  };
}

/** Prefer server counts when present, but always derive current count from member.status. */
export function resolveMemberCounts(
  members: LscMember[],
  serverCounts?: Partial<LscMemberCounts> | null
): LscMemberCounts {
  const derived = computeMemberCounts(members);
  if (!serverCounts) {
    return derived;
  }
  return {
    ...derived,
    ...serverCounts,
    current_member_count: derived.current_member_count,
  };
}

export function getOverviewMemberCount(
  members: LscMember[],
  serverCounts?: Partial<LscMemberCounts> | null
): number {
  return resolveMemberCounts(members, serverCounts).current_member_count;
}

export function formatOverviewMemberDetail(
  counts: LscMemberCounts
): string | undefined {
  const parts: string[] = [];
  if (counts.pending_link_member_count > 0) {
    parts.push(
      `${counts.pending_link_member_count} pending link`
    );
  }
  if (counts.inactive_member_count > 0) {
    parts.push('Excludes inactive profiles');
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}
